import React, { Fragment, memo, useCallback, useRef } from 'react'
import tw from 'twin.macro'
import { Form, Formik, FormikProps } from 'formik'
import { SelectChangeEvent } from '@mui/material'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { CreateGigsafeRolePayload, UpdateGigsafeRolePayload } from 'src/interfaces/gigsafeUser'
import { getGigsafeRoles } from 'src/selectors/gigsafeUser'
import { GigsafeRoleFields } from './GigsafeRoleFields'
import { GigsafeRoleSubmitButton } from './GigsafeRoleSubmitButton'
import { gigsafeRoleFormValidator } from './GigsafeRoleForm.validator'
import type { GigsafeRoleFormValues } from './GigsafeRoleForm.interface'

const FORM_ID = 'gigsafe-role-form'
const EDIT_TEXT = 'Pick a role to edit it, or leave the select on Create new role to add one.'
// Without `customer_roles_manage` the modal only shows what each role grants.
const READ_ONLY_TEXT = 'Pick a role to see the permissions it grants.'

const INITIAL_VALUES: GigsafeRoleFormValues = {
  roleId: '',
  name: '',
  permissions: [],
}

interface Props {
  isOpen: boolean
  isReadOnly: boolean
  onClose(): void
  onCreate(payload: Omit<CreateGigsafeRolePayload, 'partnerId'>): void
  onUpdate(payload: Omit<UpdateGigsafeRolePayload, 'partnerId'>): void
}

/**
 * Role management for one customer: pick an existing role to edit, or leave the select on "Create
 * new role" to add one. It opens from the page header rather than sitting as a permanent card
 * because editing roles is occasional work, and a wall of permission checkboxes under the users
 * table buried the table itself.
 *
 * `Formik` wraps the `CommonModal` so the footer's submit — portalled out of the `<Form>` element
 * by MUI — still reads the built-in-role gate through context.
 *
 * Saving closes the modal, so one open handles one role: there is no "stay open on the role you
 * just created" state to carry, and the next open starts blank from the refreshed list.
 */
const GigsafeRolesModalComponent = ({ isOpen, isReadOnly, onClose, onCreate, onUpdate }: Props) => {
  const roles = useSelector(getGigsafeRoles)
  const formikRef = useRef<FormikProps<GigsafeRoleFormValues>>(null)

  // Changing the select resets the WHOLE form to the selected role's values via `resetForm`,
  // rather than field-by-field `setFieldValue` calls — otherwise `touched`/`errors` from the
  // previously selected role would leak into the newly selected one.
  const handleRoleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const roleId = event.target.value
      const role = roles.find((candidate) => {
        return candidate.id === roleId
      })

      if (!role) {
        formikRef.current?.resetForm({ values: INITIAL_VALUES })

        return
      }

      // `[...role.permissions]` copies the array: formik mutates its own values, and the array on
      // the selected role is a reference straight out of the Redux store.
      formikRef.current?.resetForm({
        values: { roleId: role.id, name: role.name, permissions: [...role.permissions] },
      })
    },
    [roles],
  )

  const handleSubmit = useCallback(
    (values: GigsafeRoleFormValues) => {
      if (values.roleId) {
        onUpdate({
          roleId: values.roleId,
          name: values.name.trim(),
          permissions: values.permissions,
        })

        return
      }

      onCreate({ name: values.name.trim(), permissions: values.permissions })
    },
    [onCreate, onUpdate],
  )

  if (!isOpen) {
    // Unmounting resets Formik, so the next open starts from a clean form.
    return null
  }

  return (
    <Formik<GigsafeRoleFormValues>
      innerRef={formikRef}
      initialValues={INITIAL_VALUES}
      validationSchema={gigsafeRoleFormValidator}
      onSubmit={handleSubmit}
    >
      <CommonModal
        isOpen={isOpen}
        title="Roles"
        text={isReadOnly ? READ_ONLY_TEXT : EDIT_TEXT}
        onClose={onClose}
        className={tw`max-w-[560px]`}
        buttons={
          <Fragment>
            <Button size="small" color="grey" onClick={onClose}>
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>
            {isReadOnly ? null : <GigsafeRoleSubmitButton formId={FORM_ID} />}
          </Fragment>
        }
      >
        <Form id={FORM_ID} data-testid="gigsafe-role-form">
          <GigsafeRoleFields isReadOnly={isReadOnly} onRoleChange={handleRoleChange} />
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const GigsafeRolesModal = memo(GigsafeRolesModalComponent)
